import { createSelector, createSlice } from "@reduxjs/toolkit";
import axiosGeneral from "helpers/axios/axiosGeneral";
import axiosUpload from "helpers/axios/axiosUpload";
import {
  getCurrentToken,
  selectCurrentUser,
  selectCurrentUserAttributes,
  selectMyGuid,
} from "./authSlice";
import {
  setEditRootError,
  setLinkPage,
  setNewlyMadeRoot,
  setRootLink,
} from "./homeStartSlice";
import {
  addSingleData,
  addSingleDataNoParentUpdate,
  deleteEffice,
  dumpEfficeDescendants,
  failEffice,
  getInit,
  getLatest,
  getLocation,
  getPinned,
  selectData,
  uncountParentSubs,
} from "./efficeCacheSlice";
import { selectCurrentActiveEffice } from "slices/sharedSelectors";
import {
  DOUBLE_CLICK_SPEED,
  MAX_EFFICE_DEPTH,
  MAX_CONCURRENT_EXPANDED_BRANCHES,
} from "constants/constants";
import { setGeneralModal, selectGuestInfo } from "./generalSlice";
import { leaveEfficeChannels } from "./channels/personalChannelSlice";
import {
  readThisEffice,
  selectMyReadEffices,
  unreadPendingEffice,
  updateReadEffice,
} from "./myReadEfficesSlice";
import {
  cleanEffice,
  isEfficeBranch,
  isNewMemberEffice,
  isPrivateEffice,
  isRootEffice,
  isTextNode,
  makeNonce,
  nameThisId,
} from "helpers/efficeHelpers";
import {
  openInfoPage,
  resetScrollService,
  resetTextSlice,
  selectInfoPage,
  setAddPeopleModal,
  setInfoPage,
} from "./textSlice";
import { hideSpaceBox, showSpaceBox } from "./spaceMediaSlice";
import ShortenedTitle from "components/General-UI/ShortenedTitle";
import { isEmptyObject } from "helpers/randomHelpers";
import { joinSpace, selectSpaceNode } from "./spaceSlice";
import history from "constants/history";
import { setupSocketConnection } from "./channels/socketSlice";
let dispatch = null;
let getState = null;
import("Store").then((module) => {
  dispatch = module.store.dispatch;
  getState = module.store.getState;
});

const initialState = {
  goToLoading: false,
  textEffices: [],
  currentActive: null,
  currentTree: {},
  depth: 0,
};
export const slice = createSlice({
  name: "efficeTree",
  initialState,
  reducers: {
    setGoToLoading: (state, action) => {
      state.goToLoading = action.payload;
    },
    setTextEffices: (state, action) => {
      state.textEffices = action.payload;
    },
    addToTextEffices: (state, action) => {
      let node = action.payload;
      if (
        state.currentActive &&
        state.currentActive === node.parent_id &&
        !state.textEffices.includes(node.id)
      ) {
        state.textEffices = [node.id].concat(state.textEffices);
      }
    },
    swapTextEffice: (state, action) => {
      let { oldId, newId } = action.payload;
      state.textEffices.forEach((elem, index) => {
        if (elem === oldId) state.textEffices[index] = newId;
      });
    },
    setCurrentActiveValue: (state, action) => {
      state.currentActive = action.payload;
    },
    setCurrentTreeUpdates: (state, action) => {
      let { textEffices, currentTree, depth } = action.payload;
      state.depth = depth;
      state.currentTree = currentTree;
      state.textEffices = textEffices;
    },
    setCurrentTree: (state, action) => {
      state.currentTree = action.payload.currentTree;
      state.depth = action.payload.depth;
    },
    addToCurrentTree: (state, action) => {
      let { location, id } = action.payload;
      let ref = state.currentTree;

      for (let i = 0; i < location.length; i++) {
        if (!ref[location[i]]) ref[location[i]] = {};
        ref = ref[location[i]];
      }

      ref[id] = null;
    },
    removeNodeFromUI: (state, action) => {
      state.textEffices = state.textEffices.filter(
        (elem) => elem !== action.payload
      );
      let location = searchTree(action.payload, state.currentTree);
      if (location) {
        let ref = state.currentTree;
        for (let i = 0; i < location.length; i++) {
          if (i >= location.length - 1) delete ref[location[i]];
          else ref = ref[location[i]];
        }
      }
    },
    resetCurrentTree: (state) => {
      state.textEffices = [];
      for (let id in state.currentTree) state.currentTree[id] = null;
      state.depth = 0;
    },
    resetEfficeTreeSlice: () => initialState,
  },
});

export const {
  setGoToLoading,
  setTextEffices,
  setCurrentActiveValue,
  addToTextEffices,
  swapTextEffice,
  setCurrentTree,
  addToCurrentTree,
  setCurrentTreeUpdates,
  removeNodeFromUI,
  resetCurrentTree,
  resetEfficeTreeSlice,
} = slice.actions;

export const setCurrentActive = (effId) => async (dispatch, getState) => {
  if (
    effId &&
    (selectMyReadEffices(getState())[effId] === undefined ||
      selectData(getState())[effId].sub_effices >
        selectMyReadEffices(getState())[effId])
  ) {
    dispatch(readThisEffice(effId));
  }
  resetScrollService();
  dispatch(setCurrentActiveValue(effId));
};

export const createNewEffice =
  (parentId, priv, content, pinned, app_id) => async (dispatch, getState) => {
    return new Promise(async (resolve, reject) => {
      try {
        let depth = searchTree(parentId, selectCurrentTree(getState())).length;
        if (pinned) depth++; // dont allow branches made too deep since we won't be able to go in it
        checkEfficeLevels(depth);
      } catch (e) {
        window.efs_debug && window.log("Wooo there boi, too depp");
        reject("You cannot branch off any further");
        return;
      }

      //calc day of start * 2^22
      let nonce = makeNonce();
      const data = new FormData();
      data.append("nonce", nonce);
      data.append("content", content);
      if (parentId) data.append("parent_id", parentId);
      if (priv) data.append("private", priv);
      if (pinned) data.append("pinned", pinned);
      if (app_id > -1) data.append("app_id", app_id);

      // For optimistic UI update
      if (!pinned) {
        let currentAttr = selectCurrentUserAttributes(getState());
        let expectedEffice = {
          avatar_key: null,
          content: content,
          created_at: new Date(),
          creator: {
            avatar_key: currentAttr.avatar_key,
            family_name: currentAttr.family_name,
            given_name: currentAttr.given_name,
            id: currentAttr.id,
          },
          editted_at: null,
          id: nonce,
          parent_id: parentId,
          permissions_id: priv
            ? nonce
            : selectData(getState())[parentId]
            ? selectData(getState())[parentId].permissions_id
            : null,
          pinned: pinned,
          sub_effices: 0,
          type: null,
          pending: true,
        };
        let cleanExpectedEffice = cleanEffice(expectedEffice);
        dispatch(addSingleDataNoParentUpdate(cleanExpectedEffice));
        dispatch(addToTextEffices(cleanExpectedEffice));
      }

      getCurrentToken().then((idToken) => {
        axiosGeneral
          .post(`/effice`, data, {
            headers: {
              Authorization: "Bearer " + idToken,
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            window.efs_debug &&
              window.log("got positive create effice response");
            window.efs_debug && window.log(JSON.stringify(response));

            let newEffice = cleanEffice(response.data.effice);
            addNode(newEffice, nonce);

            if (isTextNode(newEffice)) {
              dispatch(
                swapTextEffice({
                  oldId: nonce,
                  newId: newEffice.id,
                })
              );
              dispatch(deleteEffice({ id: nonce }));
            } else if (isPrivateEffice(newEffice)) {
              goToEffice(newEffice.id);
              openInfoPage();
              dispatch(setAddPeopleModal(newEffice.id));
            }

            resolve();
          })
          .catch((error) => {
            window.efs_debug && window.log("error creating effice: " + error);
            if (error?.response?.status === 403)
              reject(error.response.data.error.detail);
            else reject(`Error creating ${nameThisId(app_id)}.`);

            if (!pinned) {
              dispatch(failEffice(nonce));
              dispatch(uncountParentSubs(parentId));
              dispatch(unreadPendingEffice(nonce));
            }
          });
      });
    });
  };

export const editEfficeContent = (effId, content) => async (dispatch) => {
  return new Promise(async (resolve, reject) => {
    const data = new FormData();
    data.append("content", content);

    getCurrentToken().then((idToken) => {
      axiosGeneral
        .patch(`/effice/${effId}/content`, data, {
          headers: {
            Authorization: "Bearer " + idToken,
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          window.efs_debug && window.log("Successfully editted effice content");
          window.efs_debug && window.log(response);
          resolve();
        })
        .catch((error) => {
          window.efs_debug &&
            window.log("error editting effice content" + error);
          dispatch(
            setGeneralModal({
              title: "Error",
              content: "Could not edit this effice text. " + error,
            })
          );
          reject();
        });
    });
  });
};

export const uploadEfficeIcon = (effId, fileBlob) => async (dispatch) => {
  return new Promise(async (resolve, reject) => {
    const data = new FormData();
    data.append("file", fileBlob);

    getCurrentToken().then((idToken) => {
      axiosGeneral
        .post(`/effice/${effId}/icon`, data, {
          headers: {
            Authorization: "Bearer " + idToken,
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          window.efs_debug && window.log("Successfully uploaded effice icon");
          window.efs_debug && window.log(response);
          resolve();
        })
        .catch((error) => {
          window.efs_debug && window.log("error uploading effice icon" + error);
          reject(error);
        });
    });
  });
};

export const deleteEfficeIcon = (effId) => async (dispatch) => {
  return new Promise(async (resolve, reject) => {
    getCurrentToken().then((idToken) => {
      axiosGeneral
        .delete(`/effice/${effId}/icon`, {
          headers: {
            Authorization: "Bearer " + idToken,
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          window.efs_debug && window.log("Successfully deleted effice icon");
          window.efs_debug && window.log(response);
          resolve();
        })
        .catch((error) => {
          window.efs_debug && window.log("error deleting effice icon" + error);
          reject(error);
        });
    });
  });
};

export const deleteEfficeRequest = (effId) => async (dispatch) => {
  return new Promise(async (resolve, reject) => {
    getCurrentToken().then((idToken) => {
      axiosGeneral
        .delete(`/effice/${effId}/content`, {
          headers: {
            Authorization: "Bearer " + idToken,
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          // Don't do anything because the websocket message will then clean up
          window.efs_debug && window.log("Successfully deleted effice");
          window.efs_debug && window.log(response);
          resolve();
        })
        .catch((error) => {
          window.efs_debug &&
            window.log("error deleting effice content" + error);
          dispatch(
            setGeneralModal({
              title: "Error",
              content: "Could not delete this effice. " + error,
            })
          );
          reject();
        });
    });
  });
};

export const newRootMaker = (content, avatar_blob) => async (dispatch) =>
  new Promise((res) => {
    let nonce = makeNonce();
    const data = new FormData();
    data.append("nonce", nonce);
    data.append("file", avatar_blob);
    data.append("content", content);
    data.append("private", true);
    data.append("pinned", true);

    getCurrentToken().then((idToken) => {
      axiosUpload
        .post(`/effice`, data, {
          headers: {
            Authorization: "Bearer " + idToken,
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          window.efs_debug && window.log("confirmed root effice creation: ");
          window.efs_debug && window.log(response);
          let newEffice = cleanEffice(response.data.effice);
          dispatch(setLinkPage(true));
          dispatch(setNewlyMadeRoot(newEffice));
          dispatch(setRootLink(response.data.invite_code.code));
          dispatch(addSingleData(newEffice));
          dispatch(addToCurrentTree({ location: [], id: newEffice.id }));

          res();
        })
        .catch((error) => {
          window.efs_debug && window.log("error creating root effice" + error);
          let message = "Could not create the effice. ";
          if (error.response?.data?.error?.detail)
            message += error.response.data.error.detail;
          dispatch(setEditRootError(message));
          res();
        });
    });
  });

let efficeIdClicked = null;
let doubleClick = false;

function timeout(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const doubleClickWrapper = (efficeId) =>
  new Promise(async (res, rej) => {
    if (efficeIdClicked === efficeId) {
      doubleClick = true;
      rej();
    } else {
      efficeIdClicked = efficeId;
      await timeout(DOUBLE_CLICK_SPEED);
      res(doubleClick);
      efficeIdClicked = null;
      doubleClick = false;
    }
  });

const checkEfficeLevels = (depth) => {
  if (depth > MAX_EFFICE_DEPTH) {
    dispatch(
      setGeneralModal({
        title: "You're in too deep",
        content:
          "You cannot go any deeper as you have branched off too many times.",
      })
    );
    window.efs_debug && window.log("You're in too deep");
    throw new Error("efficeDepthError");
  }
};

function searchTree(toFind, branches) {
  let result = null;
  for (let id in branches) {
    if (id === toFind) result = [id];
    else if (branches[id]) {
      let subResult = searchTree(toFind, branches[id]);
      if (subResult) result = [id].concat(subResult);
    }
  }
  return result;
}

function sumTree(tree) {
  let toSum = [];
  for (let id in tree) {
    if (tree[id]) toSum = toSum.concat(sumTree(tree[id]));
    toSum.push(id);
  }
  return toSum;
}

// Combines all nodes (tree1 - tree2) into an array
// Returns all nodes from tree 1 that are missing from tree2
function subtractTree(tree1, tree2) {
  let missing = [];

  for (let id in tree1) {
    if (tree2[id] === undefined) {
      missing.push(id);
      missing = missing.concat(sumTree(tree1[id]));
    } else if (tree2[id] === null) {
      if (tree1[id] !== null) missing = missing.concat(sumTree(tree1[id]));
    } else {
      missing = missing.concat(subtractTree(tree1[id], tree2[id]));
    }
  }
  return missing;
}

export function goToEffice(efficeId) {
  if (!selectGoToLoading(getState())) history.push(`/${efficeId}`);
  else window.efs_debug && window.log("CLICKED TOO QUICK");
}

// Wrapper to ensure double click doesn't happen and spacebox expands nicely
export async function goToNodeWrapper(efficeId) {
  if (selectGoToLoading(getState())) {
    window.efs_debug && window.log("CLICKED TOO QUICK");
    return;
  }

  dispatch(setGoToLoadingAsync(true));

  try {
    // console.log(JSON.stringify(selectData(getState()), null, 4));
    // console.log(JSON.stringify(selectCurrentRoot(getState()), null, 4));
    let passRoot =
      selectData(getState())[efficeId] &&
      isRootEffice(selectData(getState())[efficeId]) &&
      selectCurrentRoot(getState()) !== efficeId;

    await goToNode(efficeId, passRoot);

    if (selectSpaceNode(getState())?.id !== efficeId) hideSpaceBox();
    else if (!selectInfoPage(getState())) showSpaceBox();

    if (passRoot) {
      dispatch(setGoToLoading(false)); // immediately stop double click time
      if (selectInfoPage(getState())) dispatch(setInfoPage(false));
      for (var pinned in selectCurrentTree(getState())[efficeId]) {
        // await goToNode(pinned);
        goToEffice(pinned);
        break;
      }
    } else dispatch(setGoToLoadingAsync(false));
  } catch (error) {
    handleGoToNodeWrapperError(error);
    dispatch(setGoToLoadingAsync(false));
  }
}

export async function initialiseGuestView() {
  window.log("Initialising Guest View");
  const { node } = selectGuestInfo(getState());
  dispatch(addSingleData(node));
  dispatch(setCurrentActiveValue(node.id));

  try {
    const currentUser = selectCurrentUser(getState());
    if (!currentUser) await setupSocketConnection();
    joinSpace(node.id);
  } catch (e) {
    window.log("Error Initialising Guest View");
    window.log(JSON.stringify(e));
  }
}

function handleGoToNodeWrapperError(error) {
  window.efs_debug &&
    window.log("Error Going to Effice: " + JSON.stringify(error, null, 4));
  if (error?.message === "efficeDepthError") {
    history.goBack();
  } else {
    dispatch(
      setGeneralModal({
        title: error?.response?.status === 401 ? "Access Denied" : "Woops!",
        content:
          error?.response?.status === 401
            ? "You do not have access to this space."
            : "We could not find what you were looking for.",
      })
    );
    goToHome();
  }
}

export async function goToNode(nodeId, passRoot = false) {
  // debugger;
  if (selectCurrentActive(getState()) === nodeId) {
    window.efs_debug && window.log("SAME NOD");
    return;
  }
  // Go Home
  if (!nodeId) return goToEffice("home");

  // Deep copy of currentTree State
  let currentTree = JSON.parse(JSON.stringify(selectCurrentTree(getState())));
  let oldTree = JSON.parse(JSON.stringify(selectCurrentTree(getState())));
  let data = selectData(getState());

  let location = await getLocation(nodeId);
  window.efs_debug && window.log("getLocation: " + JSON.stringify(location));

  let textEffices = [];
  let depth = location.length;
  checkEfficeLevels(depth);

  // Going through and reconstructing currentTree from the ground up

  // Keep the Root Effices
  for (let i in currentTree) currentTree[i] = null;

  // Start iterating over the location array
  let ref = currentTree;
  for (let i = 0; i < depth; i++) {
    ref = ref[location[i]] = {};
    if (i === depth - 1) {
      let { latest, pinned } = await getInit(nodeId);
      for (let j = 0; j < pinned.length; j++) ref[pinned[j]] = null;
      textEffices = latest;
    } else if (depth - i <= MAX_CONCURRENT_EXPANDED_BRANCHES) {
      let pinned = await getPinned(location[i]);
      for (let j = 0; j < pinned.length; j++) ref[pinned[j]] = null;
    }
  }

  // CurrentACtive is separate from updates since we will be doing extra work
  if (!(passRoot && currentTree && !isEmptyObject(currentTree[nodeId])))
    dispatch(setCurrentActive(nodeId));
  dispatch(
    setCurrentTreeUpdates({
      textEffices,
      currentTree,
      depth,
    })
  );

  window.efs_debug && window.log(JSON.stringify(oldTree, null, 4));
  window.efs_debug && window.log(JSON.stringify(currentTree, null, 4));
  window.efs_debug &&
    window.log(
      subtractTree(oldTree, currentTree).filter(
        (bid) => data[bid] && isPrivateEffice(data[bid])
      )
    );
  leaveEfficeChannels(
    subtractTree(oldTree, currentTree).filter(
      (bid) => data[bid] && isPrivateEffice(data[bid])
    )
  );

  // when getting init and pinned, we already rejoinEfficeChannels, so no need to do it here
}

export const goToHome = () => {
  // console.log("GOING TO HOME");

  if (!selectCurrentUser(getState())) {
    history.push("/signIn");
    return;
  }
  let oldTree = JSON.parse(JSON.stringify(selectCurrentTree(getState())));

  hideSpaceBox();
  dispatch(setCurrentActive(null));
  dispatch(resetCurrentTree());
  dispatch(resetTextSlice());

  let currentTree = selectCurrentTree(getState());
  let data = selectData(getState());
  let toLeave = [];
  for (let rootId in currentTree) {
    if (oldTree[rootId] && !isEmptyObject(oldTree[rootId]))
      toLeave = toLeave.concat(
        sumTree(oldTree[rootId]).filter(
          (nodeId) => data[nodeId] && isPrivateEffice(data[nodeId])
        )
      );
  }
  leaveEfficeChannels(toLeave);
};
window.goToHome = goToHome;

export function goToParentEffice() {
  if (!selectCurrentActive(getState())) return;
  goToEffice(selectCurrentActiveEffice(getState()).parent_id);
}

export const addNode = (effice, nonce = null) => {
  dispatch(addSingleData(effice));

  if (
    !isRootEffice(effice) &&
    !isPrivateEffice(effice) &&
    !isNewMemberEffice(effice)
  ) {
    if (effice.creator.id === selectMyGuid(getState()))
      dispatch(
        updateReadEffice({
          effId: effice.parent_id,
          read_subs: selectData(getState())[effice.parent_id].sub_effices,
        })
      );
    else if (effice.parent_id === selectCurrentActive(getState()))
      dispatch(readThisEffice(effice.parent_id));
  }

  if (isRootEffice(effice)) {
    dispatch(addToCurrentTree({ location: [], id: effice.id }));
  } else if (isEfficeBranch(effice)) {
    let newLocation = searchTree(
      effice.parent_id,
      selectCurrentTree(getState())
    );
    if (newLocation) {
      let myLocation = searchTree(
        selectCurrentActive(getState()),
        selectCurrentTree(getState())
      );
      if (
        myLocation &&
        myLocation.includes(effice.parent_id) &&
        myLocation.length - newLocation.length <
          MAX_CONCURRENT_EXPANDED_BRANCHES
      ) {
        dispatch(addToCurrentTree({ location: newLocation, id: effice.id }));
      }
    }
  } else if (
    // Make sure we dont add an effice if the current tab is the one creating a effice
    // (so as to ensure that the promise flow confirms or fails an attempted effice creation and we dont add the same effice into the UI twice)
    // Only text effices are being optimistically updated
    effice.parent_id === selectCurrentActive(getState()) &&
    !selectTextEffices(getState()).includes(nonce) // so we don't add it if we are creating it, because promise will.
  ) {
    dispatch(addToTextEffices(effice));
  }
};

export const deleteEfficeAndCleanUp =
  (effice, deleted) => async (dispatch, getState) => {
    let data = selectData(getState());
    if (!data[effice.id]) {
      return; // TODO
    }

    let currentLocation = searchTree(
      selectCurrentActive(getState()),
      selectCurrentTree(getState())
    );
    let index = currentLocation.indexOf(effice.id);

    if (index > -1) {
      dispatch(resetTextSlice());

      // if index < 0 it will just go home which is what we want
      goToEffice(currentLocation[index - 1]);

      // If its been deleted OR we have been kicked
      if (deleted) {
        dispatch(
          setGeneralModal({
            title: "Successfully Deleted",
            content: (
              <>
                <span className="font-bold">
                  <ShortenedTitle
                    title={data[effice.id].content}
                    maxChars={100}
                  />
                </span>
                &nbsp;has been removed and no longer exists.
              </>
            ),
          })
        );
      } else {
        dispatch(
          setGeneralModal({
            title: "You Were Removed",
            content: (
              <>
                You are no longer a part of&nbsp;
                <span className="font-bold">
                  <ShortenedTitle
                    title={data[effice.id].content}
                    maxChars={100}
                  />
                </span>
                &nbsp;and can no longer view its content.
              </>
            ),
          })
        );
      }
    }

    if (isPrivateEffice(effice)) dispatch(dumpEfficeDescendants([effice.id]));
    dispatch(removeNodeFromUI(effice.id));
    dispatch(deleteEffice(effice));
  };

// let setGoToLoadingAsyncTimer = null;
export const setGoToLoadingAsync = (payload) => async (dispatch) => {
  if (payload === false) {
    setTimeout(() => {
      dispatch(setGoToLoading(payload));
    }, DOUBLE_CLICK_SPEED);
  } else {
    dispatch(setGoToLoading(payload));
  }
};

export const getEarlierSubs = () => async (dispatch, getState) => {
  return new Promise((res, rej) => {
    // dispatch(setLoadingTextEffices(true));
    let before = null;
    let textEffices = selectTextEffices(getState());
    let textLength = textEffices.length;
    if (textEffices[textLength - 1]) {
      before = textEffices[textLength - 1];
    } else {
      return;
    }
    window.efs_debug && window.log("GET EARLIER SUBS");
    let currentActive = getState().efficeTree.currentActive;
    getLatest(currentActive, before)
      .then((latest) => {
        dispatch(setTextEffices(textEffices.concat(latest)));
        // dispatch(setLoadingTextEffices(false));
        res(latest.length);
      })
      .catch(() => {
        // dispatch(setLoadingTextEffices(false));
        rej();
      });
  });
};

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state) => state.counter.value)
export const selectTextEffices = (state) => state.efficeTree.textEffices;
export const selectCurrentRoot = (state) => {
  let tree = state.efficeTree.currentTree;
  for (var id in tree) if (tree[id]) return id;
  return null;
};
export const selectCurrentTree = (state) => state.efficeTree.currentTree;
export const selectDepth = (state) => state.efficeTree.depth;
export const selectCurrentActive = (state) => state.efficeTree.currentActive;
export const selectGoToLoading = (state) => state.efficeTree.goToLoading;
export const selectCurrentActiveAlive = createSelector(
  (state) => state.efficeTree.currentActive,
  (currentActive) => currentActive !== null && currentActive !== undefined
);

export default slice.reducer;
